import { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { BLOCK_DEFINITIONS } from './blockTypes';
import { toast } from 'react-toastify';
import './blocktypesconfig.css';

const API_BASE = process.env.REACT_APP_URL || 'http://localhost:3501';

export default function BlockTypesConfig({ authToken, onConfigChanged }) {
  const [enabledTypes, setEnabledTypes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load current configuration
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const res = await fetch(`${API_BASE}/pagebuilder/blocktypes`, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`,
          }
        });
        if (res.ok) {
          const data = await res.json();
          setEnabledTypes(new Set(data?.enabled || BLOCK_DEFINITIONS.map(d => d.type)));
        } else {
          // Default: all types enabled if endpoint doesn't exist
          setEnabledTypes(new Set(BLOCK_DEFINITIONS.map(d => d.type)));
        }
      } catch (err) {
        console.error('Error loading block types config:', err);
        // Default: all types enabled
        setEnabledTypes(new Set(BLOCK_DEFINITIONS.map(d => d.type)));
      } finally {
        setLoading(false);
      }
    };

    loadConfig();
  }, [authToken]);

  const toggleBlockType = (type) => {
    const newEnabled = new Set(enabledTypes);
    if (newEnabled.has(type)) {
      newEnabled.delete(type);
    } else {
      newEnabled.add(type);
    }
    setEnabledTypes(newEnabled);
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/pagebuilder/blocktypes`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`,
        },
        body: JSON.stringify({ enabled: Array.from(enabledTypes) })
      });
      if (res.ok) {
        toast.success('Block types configuration saved!', { position: 'bottom-left' });
        if (onConfigChanged) onConfigChanged(Array.from(enabledTypes));
      } else {
        toast.error('Failed to save block types configuration', { position: 'bottom-left' });
      }
    } catch (err) {
      console.error('Error saving config:', err);
      toast.error('Error saving configuration', { position: 'bottom-left' });
    } finally {
      setSaving(false);
    }
  };

  const enableAll = () => {
    setEnabledTypes(new Set(BLOCK_DEFINITIONS.map(d => d.type)));
  };

  const disableAll = () => {
    setEnabledTypes(new Set());
  };

  if (loading) {
    return <div className="pagebuilder-config-loading">Loading block types...</div>;
  }

  const groupedBlocks = {
    'Existing': BLOCK_DEFINITIONS.filter(d => !['hero', 'text', 'heading', 'paragraph', 'quote', 'callout', 'highlightBox', 'badge', 'bulletList', 'iconList', 'faq', 'accordion', 'toggleBlock', 'banner', 'dataCard', 'columns', 'cardGrid', 'audio', 'pdf', 'navMenu', 'submenu', 'breadcrumbs', 'footer', 'button', 'countdown', 'poll', 'announcement'].includes(d.type)),
    'Typography': BLOCK_DEFINITIONS.filter(d => ['heading', 'paragraph', 'quote', 'callout', 'highlightBox', 'badge'].includes(d.type)),
    'Lists & Accordions': BLOCK_DEFINITIONS.filter(d => ['bulletList', 'iconList', 'faq', 'accordion', 'toggleBlock'].includes(d.type)),
    'Layout': BLOCK_DEFINITIONS.filter(d => ['banner', 'dataCard', 'columns', 'cardGrid'].includes(d.type)),
    'Media': BLOCK_DEFINITIONS.filter(d => ['audio', 'pdf'].includes(d.type)),
    'Navigation': BLOCK_DEFINITIONS.filter(d => ['navMenu', 'submenu', 'breadcrumbs', 'footer', 'button'].includes(d.type)),
    'Interactive': BLOCK_DEFINITIONS.filter(d => ['countdown', 'poll', 'announcement'].includes(d.type)),
  };

  return (
    <div className="pagebuilder-blocktypes-config">
      <div className="pagebuilder-config-header">
        <h3>Configure Available Block Types</h3>
        <p className="pagebuilder-config-description">
          Select which block types should be available when adding blocks to pages.
        </p>
      </div>

      <div className="pagebuilder-config-actions">
        <button
          type="button"
          className="pagebuilder-add-btn pagebuilder-add-btn--sm"
          onClick={enableAll}
          title="Enable all block types"
        >
          <i className="bi bi-check-all" aria-hidden="true" /> Enable All
        </button>
        <button
          type="button"
          className="pagebuilder-add-btn pagebuilder-add-btn--sm"
          onClick={disableAll}
          title="Disable all block types"
        >
          <i className="bi bi-x-circle" aria-hidden="true" /> Disable All
        </button>
        <button
          type="button"
          className="pagebuilder-add-btn pagebuilder-add-btn--sm"
          onClick={saveConfig}
          disabled={saving}
          title="Save configuration"
        >
          <i className="bi bi-cloud-check" aria-hidden="true" /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>

      <div className="pagebuilder-blocktypes-list">
        {Object.entries(groupedBlocks).map(([group, blocks]) => (
          blocks.length > 0 && (
            <div key={group} className="pagebuilder-blocktypes-group">
              <h4 className="pagebuilder-blocktypes-group-title">{group}</h4>
              <div className="pagebuilder-blocktypes-items">
                {blocks.map((def) => (
                  <div key={def.type} className="pagebuilder-blocktype-item">
                    <Form.Check
                      type="checkbox"
                      id={`blocktype-${def.type}`}
                      label={
                        <span className="pagebuilder-blocktype-label">
                          <i className={`${def.icon} pagebuilder-blocktype-icon`} aria-hidden="true" />
                          <span className="pagebuilder-blocktype-name">{def.label}</span>
                        </span>
                      }
                      checked={enabledTypes.has(def.type)}
                      onChange={() => toggleBlockType(def.type)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )
        ))}
      </div>

      <div className="pagebuilder-config-footer">
        <p className="pagebuilder-config-summary">
          {enabledTypes.size} of {BLOCK_DEFINITIONS.length} block types enabled
        </p>
      </div>
    </div>
  );
}
